import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, formatPercent, formatQuantity, type PublicOffer } from '@kontor/shared';
import { api, ApiError } from '../../api';
import './offers.css';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const day = (d: string) => dateFormat.format(new Date(`${d}T12:00:00`));

/**
 * Öffentliche Seite eines Angebots für den Kunden (KON-102). Er erreicht sie über den Link aus der E-Mail, ohne Anmeldung. Er sieht das Angebot, lädt das PDF
 * und nimmt an oder lehnt ab. Der Link ist das einzige Geheimnis, deshalb steht auf der Seite nichts über andere Angebote oder den Mandanten hinaus.
 */
export function PublicOfferPage() {
  const { token = '' } = useParams();
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const offer = useQuery({ queryKey: ['public-offer', token], queryFn: async () => (await api<{ data: PublicOffer }>('GET', `/public/offers/${token}`)).data, retry: false });
  const decide = useMutation({
    mutationFn: async (step: 'accept' | 'reject') =>
      (await api<{ data: PublicOffer }>('POST', `/public/offers/${token}/${step}`, step === 'accept' ? { name: name.trim(), confirm } : { reason: reason.trim() || undefined })).data,
    onSuccess: (o) => client.setQueryData(['public-offer', token], o),
    onError: (e) => setError(e instanceof ApiError ? (e.status === 429 ? 'Zu viele Versuche. Bitte warten Sie einige Minuten.' : (Object.values(e.fields)[0] ?? e.message)) : 'Das hat nicht geklappt. Bitte versuchen Sie es noch einmal.'),
  });

  if (offer.isLoading) return <div className="center-screen">Wird geladen …</div>;
  if (offer.isError || !offer.data) {
    return (
      <div className="center-screen">
        <div className="card narrow">
          <h1>Angebot nicht verfügbar</h1>
          <p className="muted">Der Link ist ungültig oder wurde gesperrt. Bitte wenden Sie sich an das Unternehmen, das Ihnen das Angebot geschickt hat.</p>
        </div>
      </div>
    );
  }
  const o = offer.data;
  const r = o.recipient;

  function accept(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (name.trim().length < 2) return setError('Bitte geben Sie Ihren Namen an.');
    if (!confirm) return setError('Bitte bestätigen Sie die Annahme mit dem Häkchen.');
    decide.mutate('accept');
  }

  return (
    <div className="public-offer">
      <header className="public-offer__head">
        <img src={`/api/v1/public/offers/${token}/logo`} alt="" className="public-offer__logo" onError={(e) => (e.currentTarget.style.display = 'none')} />
        <strong>{o.companyName}</strong>
      </header>
      <main className="public-offer__main">
        <div className="card">
          <div className="offer-head">
            <div>
              <div className="muted small">Angebot für</div>
              <strong>{r.name}</strong>
              {r.company && r.company !== r.name && <div>{r.company}</div>}
              <div>{r.street}</div>
              <div>{[r.postalCode, r.city].filter(Boolean).join(' ')}</div>
            </div>
            <dl className="offer-meta">
              <dt>Angebot</dt>
              <dd>{o.number}</dd>
              <dt>Datum</dt>
              <dd>{dateFormat.format(new Date(o.issuedAt))}</dd>
              {o.validUntil && (<><dt>Gültig bis</dt><dd>{day(o.validUntil)}</dd></>)}
            </dl>
          </div>
          <h1 className="public-offer__subject">{o.subject}</h1>
          {o.introText && <p className="offer-text">{o.introText}</p>}
          <table className="table table--cards offer-table">
            <thead>
              <tr>
                <th>Pos.</th>
                <th>Bezeichnung</th>
                <th className="num">Menge</th>
                <th className="num">Einzelpreis</th>
                <th className="num">Rabatt</th>
                <th className="num">USt.</th>
                <th className="num">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {o.lines.map((l) => (
                <tr key={l.position}>
                  <td data-label="Pos.">{l.position}</td>
                  <td>
                    <strong>{l.title}</strong>
                    {l.description && <div className="muted small offer-desc">{l.description}</div>}
                  </td>
                  <td data-label="Menge" className="num">{formatQuantity(l.quantityMilli)} {l.unit}</td>
                  <td data-label="Einzelpreis" className="num">{formatCents(l.unitPriceCents)}</td>
                  <td data-label="Rabatt" className="num">{l.discountBp ? formatPercent(l.discountBp) : '–'}</td>
                  <td data-label="USt." className="num">{o.smallBusiness ? '–' : `${l.taxRate} %`}</td>
                  <td data-label="Betrag" className="num">{formatCents(l.amountCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <dl className="offer-sum">
            {o.totals.discountCents !== 0 && (
              <>
                <dt>Summe der Positionen</dt>
                <dd>{formatCents(o.totals.linesCents)}</dd>
                <dt>abzüglich {formatPercent(o.discountBp)} Rabatt</dt>
                <dd>– {formatCents(o.totals.discountCents)}</dd>
              </>
            )}
            <dt>Netto</dt>
            <dd>{formatCents(o.totals.netCents)}</dd>
            {!o.smallBusiness &&
              o.totals.groups.filter((g) => g.rate > 0).map((g) => (
                <div key={g.rate} className="offer-sum__row">
                  <dt>{g.rate} % USt. auf {formatCents(g.netCents)}</dt>
                  <dd>{formatCents(g.taxCents)}</dd>
                </div>
              ))}
            <dt className="offer-totals__gross">Gesamt brutto</dt>
            <dd className="offer-totals__gross">{formatCents(o.totals.grossCents)}</dd>
          </dl>
          {o.smallBusiness && <p className="muted small">Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.</p>}
          {o.closingText && <p className="offer-text">{o.closingText}</p>}
          <a className="btn btn--secondary" href={`/api/v1/public/offers/${token}/pdf`} target="_blank" rel="noreferrer">Als PDF herunterladen</a>
        </div>

        {o.status === 'accepted' && (
          <div className="alert alert--ok" role="status">
            Sie haben dieses Angebot {o.decidedAt ? `am ${dateFormat.format(new Date(o.decidedAt))} ` : ''}angenommen{o.decidedByName && o.decidedByName !== 'Kunde' ? ` (${o.decidedByName})` : ''}. Vielen Dank, wir melden uns bei Ihnen.
          </div>
        )}
        {o.status === 'rejected' && <div className="alert alert--warn" role="status">Dieses Angebot wurde abgelehnt. Wenn Sie es sich anders überlegen, sprechen Sie uns gern an.</div>}
        {o.status === 'expired' && <div className="alert alert--warn" role="status">Dieses Angebot ist abgelaufen. Bitte fragen Sie bei uns nach einem neuen Angebot.</div>}
        {o.status === 'superseded' && <div className="alert alert--warn" role="status">Zu diesem Angebot gibt es eine neuere Fassung. Bitte nutzen Sie den Link aus der letzten E-Mail.</div>}

        {o.canDecide && !rejecting && (
          <form className="card" onSubmit={accept} noValidate>
            <h2>Angebot annehmen</h2>
            {error && <div className="alert alert--error" role="alert">{error}</div>}
            <label className="field">
              <span>Ihr Name</span>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={160} autoComplete="name" />
            </label>
            <label className="check">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
              <span>Ich nehme das Angebot {o.number} über {formatCents(o.totals.grossCents)} verbindlich an.</span>
            </label>
            <div className="form-actions">
              <button className="btn btn--primary" type="submit" disabled={decide.isPending}>{decide.isPending ? 'Wird gesendet …' : 'Verbindlich annehmen'}</button>
              <button className="btn btn--secondary" type="button" onClick={() => { setRejecting(true); setError(null); }}>Ablehnen</button>
            </div>
          </form>
        )}
        {o.canDecide && rejecting && (
          <form className="card" onSubmit={(e) => { e.preventDefault(); setError(null); decide.mutate('reject'); }} noValidate>
            <h2>Angebot ablehnen</h2>
            {error && <div className="alert alert--error" role="alert">{error}</div>}
            <label className="field">
              <span>Grund (freiwillig)</span>
              <textarea className="input textarea" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
              <small className="muted">Ein kurzer Hinweis hilft uns, ein passenderes Angebot zu machen.</small>
            </label>
            <div className="form-actions">
              <button className="btn btn--danger" type="submit" disabled={decide.isPending}>{decide.isPending ? 'Wird gesendet …' : 'Ablehnen'}</button>
              <button className="btn btn--secondary" type="button" onClick={() => setRejecting(false)}>Zurück</button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
